/**
 * One logical email send, as returned by `EmailSendsController.SendLog`. Recipients/retry
 * attempts are already collapsed server-side into one row per `sendCorrelationId`.
 */
export interface SendLogEntry {
    sendCorrelationId: string;
    sentOn: string;
    recipients: string[];
    /** "Sent" | "Failed" | "Pending" */
    status: string;
    /** Set only when `status` is "Failed"; one entry per failed recipient. */
    failureSummary?: string | null;
    /** Generic display title resolved server-side; the research question for KboFlowRun sends. */
    title: string;
    /** The source run, supplied for KboFlowRun sends so Admin can inspect it. */
    kboFlowRunId?: string | null;
    /** The source question, when the delivered report can be resolved exactly. */
    questionId?: string | null;
}

/** A page of `SendLogEntry`, for the server-side paginated Send Log grid. */
export interface SendLogPage {
    items: SendLogEntry[];
    totalCount: number;
    page: number;
    pageSize: number;
}

/** The immutable email content recorded when a logical send was attempted. */
export interface SendLogEmailPreview {
    sendCorrelationId: string;
    subject: string;
    htmlBody: string;
    plaintextBody: string;
}

export type SendLogStatusPresentation = {
    color: "blue" | "red" | "yellow";
    label: string;
};

/** Maps the backend's Sent/Failed/Pending status string to chip presentation. */
export function getSendStatusPresentation(
    status?: string | null,
): SendLogStatusPresentation {
    switch (status?.trim().toLowerCase()) {
        case "sent":
            return { color: "blue", label: "Sent" };
        case "failed":
            return { color: "red", label: "Failed" };
        default:
            return { color: "yellow", label: "Pending" };
    }
}
